import { computed, type MaybeRefOrGetter, ref, toValue } from "vue";

import { API } from "@/api/api";
import { useBaseView } from "@/composables/useBaseView";
import { RouteStopFileType } from "@/constants/trip";

import type { StopInfoFile } from "@/types/trip";

export function useRouteStopFiles(
  files: MaybeRefOrGetter<StopInfoFile[]>,
  tripId: MaybeRefOrGetter<number>,
  routeStopId: MaybeRefOrGetter<string | number | null | undefined>,
  fileType: RouteStopFileType,
) {
  const api = new API();
  const { getPreviewPath } = useBaseView();

  const initialFiles = ref<StopInfoFile[]>([]);

  const routeStopTag = computed<string>(() => "route_stop:" + toValue(routeStopId));

  const uploadedFiles = computed<StopInfoFile[]>(() => toValue(files).filter((file) => !file.file));

  const newFiles = computed<StopInfoFile[]>(() => toValue(files).filter((file) => Boolean(file.file)));

  const retaggedFiles = computed<StopInfoFile[]>(() => {
    return toValue(files).filter((file) => !file.file && file.bol !== getTaggedBol(file));
  });

  const removedFiles = computed<StopInfoFile[]>(() => {
    const fileIds = new Set(toValue(files).map((file) => file.id));

    console.log("files", toValue(files));

    console.log("initial files", initialFiles.value);

    return initialFiles.value.filter((file) => !fileIds.has(file.id));
  });

  function isFileOfType(file: StopInfoFile): boolean {
    return Boolean(file.tags?.some((tag) => tag === fileType || tag.startsWith(fileType + ":")));
  }

  function getTaggedBol(file: StopInfoFile): string | undefined {
    return file.tags?.find((tag) => tag.startsWith("bol:"))?.split(":")[1];
  }

  function getBolTags(file: StopInfoFile): string[] {
    const tags = (file.tags ?? []).filter((tag) => tag !== "bol" && !tag.startsWith("bol:"));

    return [...tags, file.bol ? "bol:" + file.bol : "bol"];
  }

  function getTagsWithoutRouteStop(file: StopInfoFile): string[] {
    return (file.tags ?? []).filter((tag) => tag !== routeStopTag.value);
  }

  async function loadFiles(): Promise<StopInfoFile[]> {
    try {
      const response = await api.trips.getTripFiles(toValue(tripId));

      if (response.success && response.data) {
        const routeStopFiles = response.data
          .filter((file) => file.tags?.includes(routeStopTag.value) && isFileOfType(file))
          .map((file) => ({
            ...file,
            previewPath: getPreviewPath(file),
            bol: getTaggedBol(file),
          }));

        initialFiles.value = [...routeStopFiles];

        return routeStopFiles;
      }
    } catch (error) {
      console.error(error);
    }

    return [];
  }

  async function uploadFiles(): Promise<void> {
    const uploadPromises = newFiles.value.map(async (uploadingFile) => {
      uploadingFile.loading = true;

      try {
        const response = await api.trips.uploadRouteStopFile(
          toValue(tripId),
          String(toValue(routeStopId)),
          fileType,
          uploadingFile.file!,
          fileType === RouteStopFileType.bol ? uploadingFile.bol : "dummy",
        );

        if (response.success && response.data) {
          Object.assign(uploadingFile, {
            ...response.data,
            previewPath: getPreviewPath(response.data),
          });
          initialFiles.value = [...initialFiles.value, uploadingFile];
        } else {
          uploadingFile.error = true;
        }
      } catch (error) {
        console.error(error);
        uploadingFile.error = true;
      } finally {
        uploadingFile.loading = false;
      }
    });

    await Promise.all(uploadPromises);
  }

  async function retagFiles(): Promise<void> {
    const retagPromises = retaggedFiles.value.map(async (retaggingFile) => {
      retaggingFile.loading = true;

      try {
        const response = await api.files.updateFileTags(retaggingFile.id, getBolTags(retaggingFile));

        if (response.success && response.data) {
          Object.assign(retaggingFile, {
            ...response.data,
            previewPath: getPreviewPath(response.data),
          });
        } else {
          retaggingFile.error = true;
        }
      } catch (error) {
        console.error(error);
        retaggingFile.error = true;
      } finally {
        retaggingFile.loading = false;
      }
    });

    await Promise.all(retagPromises);
  }

  async function removeFiles(): Promise<void> {
    const removePromises = removedFiles.value.map(async (removingFile) => {
      try {
        const response = await api.files.updateFileTags(removingFile.id, getTagsWithoutRouteStop(removingFile));

        if (response.success) {
          initialFiles.value = initialFiles.value.filter((file) => file.id !== removingFile.id);
        }
      } catch (error) {
        console.error(error);
      }
    });

    await Promise.all(removePromises);
  }

  async function saveFiles(): Promise<void> {
    await Promise.all([uploadFiles(), retagFiles(), removeFiles()]);
  }

  return {
    uploadedFiles,
    newFiles,
    retaggedFiles,
    removedFiles,
    loadFiles,
    saveFiles,
  };
}
